import { useEffect, useState } from 'react';
import { api } from '@/api/client';
import { useAuth } from '@/auth/AuthContext';
import { missingMcpScopesLabel } from '@/components/McpOAuthCard';

const SCOPES = [
  { code: 'CLIENT_VIEW', label: 'Odczyt klientów' },
  { code: 'CLIENT_EDIT', label: 'Dodawanie i edycja klientów', requires: 'CLIENT_VIEW' },
  { code: 'INTAKE_VIEW', label: 'Odczyt ujęć' },
  { code: 'INTAKE_CREATE', label: 'Dodawanie ujęć', requires: 'INTAKE_VIEW' },
  { code: 'INTAKE_EDIT', label: 'Edycja ujęć', requires: 'INTAKE_VIEW' },
  { code: 'CONTRACT_VIEW', label: 'Odczyt umów' },
  { code: 'CONTRACT_CREATE', label: 'Dodawanie umów', requires: 'CONTRACT_VIEW' },
  { code: 'CONTRACT_EDIT', label: 'Edycja szkiców umów', requires: 'CONTRACT_VIEW' },
  { code: 'OFFER_VIEW', label: 'Odczyt ofert' },
  { code: 'OFFER_CREATE', label: 'Dodawanie ofert', requires: 'OFFER_VIEW' },
  { code: 'OFFER_EDIT', label: 'Edycja ofert i ich statusu', requires: 'OFFER_VIEW' },
  { code: 'STUDY_VIEW', label: 'Odczyt opracowań' },
  { code: 'STUDY_CREATE', label: 'Dodawanie opracowań', requires: 'STUDY_VIEW' },
  { code: 'STUDY_EDIT', label: 'Edycja opracowań (bez statusu i kroków)', requires: 'STUDY_VIEW' },
] as const;

interface TokenView {
  id: number;
  name: string;
  scopes: string[];
  createdAt: string;
  expiresAt: string;
  revokedAt: string | null;
}

const ENDPOINT = '/api/auth/mcp-tokens';
const date = (value: string) => new Date(value).toLocaleString('pl-PL');
const LAUNCH_COMMAND = `bash -c 'read -rsp "Token MCP: " HYDROWOLSKI_TOKEN && printf "\\n" && export HYDROWOLSKI_TOKEN && exec claude'`;

export function McpTokensCard() {
  const { user, impersonation } = useAuth();
  const [tokens, setTokens] = useState<TokenView[]>([]);
  const [name, setName] = useState('');
  const [days, setDays] = useState(30);
  const [scopes, setScopes] = useState<string[]>([]);
  const [secret, setSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<'token' | 'endpoint' | 'command' | 'launch' | 'config' | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [revokeId, setRevokeId] = useState<number | null>(null);
  const remoteEndpoint = `${window.location.origin}/api/mcp`;
  const remoteConfig = {
    type: 'http',
    url: remoteEndpoint,
    headers: { Authorization: 'Bearer ${HYDROWOLSKI_TOKEN}' },
  };
  const addCommand = `claude mcp add-json --scope user hydrowolski '${JSON.stringify(remoteConfig)}'`;
  const configJson = JSON.stringify({ mcpServers: { hydrowolski: remoteConfig } }, null, 2);

  useEffect(() => {
    let active = true;
    setTokens([]); setSecret(null); setScopes([]); setError(null); setRevokeId(null);
    if (impersonation || !user) { setLoading(false); return; }
    setLoading(true);
    api.get<TokenView[]>(ENDPOINT)
      .then(data => { if (active) setTokens(data); })
      .catch(err => { if (active) setError(err instanceof Error ? err.message : 'Nie udało się pobrać tokenów.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [user?.id, impersonation]);

  function toggle(code: string, checked: boolean) {
    setScopes(current => {
      const next = new Set(current);
      if (checked) {
        next.add(code);
        const scope = SCOPES.find(item => item.code === code);
        if (scope && 'requires' in scope) next.add(scope.requires);
      } else {
        next.delete(code);
        SCOPES.forEach(scope => { if ('requires' in scope && scope.requires === code) next.delete(scope.code); });
      }
      return [...next];
    });
  }

  async function create(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true); setError(null); setCopied(null);
    try {
      const result = await api.post<{ token: TokenView; secret: string }>(ENDPOINT,
        { name, expiresInDays: days, scopes });
      setSecret(result.secret);
      setTokens(current => [result.token, ...current]);
      setName('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Nie udało się utworzyć tokenu.');
    } finally { setBusy(false); }
  }

  async function revoke(id: number) {
    setBusy(true); setError(null);
    try {
      await api.del(`${ENDPOINT}/${id}`);
      setTokens(current => current.map(token => token.id === id
        ? { ...token, revokedAt: new Date().toISOString() } : token));
      setRevokeId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Nie udało się unieważnić tokenu.');
    } finally { setBusy(false); }
  }

  async function copy(value: string, target: NonNullable<typeof copied>) {
    try { await navigator.clipboard.writeText(value); setCopied(target); }
    catch { setError('Nie można skopiować automatycznie. Zaznacz i skopiuj tekst z pola.'); }
  }

  return (
    <section className="card mb-6" aria-labelledby="mcp-tokens-heading">
      <div className="card-head"><h2 id="mcp-tokens-heading" className="card-title">Agenci MCP (połączenie zdalne)</h2></div>
      <div className="card-body space-y-4 text-[13px]">
        <p className="text-ink-600">Claude Code na Twoim komputerze łączy się bezpośrednio z portalem przez HTTPS.
          Serwer MCP działa w portalu; nie wymaga lokalnego Node.js ani dodatkowego serwera.
          Wybierz zakres dostępu oraz termin
          ważności. Agent działa z Twoimi aktualnymi uprawnieniami. Token możesz w każdej chwili unieważnić.</p>
        {impersonation ? <p className="text-ink-600">Wróć na własne konto, aby zarządzać tokenami agentów.</p> : <>
          {error && <p role="alert" className="p-3 bg-red-100 text-red-500 rounded-md">{error}</p>}
          <div className="border border-ink-100 rounded-md p-3 space-y-2">
            <label className="field"><span className="field-label">Adres zdalnego MCP (Streamable HTTP)</span>
              <input className="input font-mono text-xs" value={remoteEndpoint} readOnly
                onFocus={event => event.currentTarget.select()} /></label>
            <button className="btn btn-secondary btn-sm" onClick={() => void copy(remoteEndpoint, 'endpoint')}>
              {copied === 'endpoint' ? 'Skopiowano adres' : 'Kopiuj adres MCP'}</button>
            <p className="text-ink-500">Użyj adresu HTTPS wdrożonego portalu. Klient uwierzytelnia się tokenem Bearer.</p>
          </div>
          {secret && <div className="border border-water-200 rounded-md p-3 space-y-2">
            <p className="font-semibold">Skopiuj token teraz - pokażemy go tylko raz.</p>
            <p>Przechowaj token prywatnie. Przy uruchamianiu Claude Code wklej go dopiero przy pytaniu
              „Token MCP:” w instrukcji poniżej. Konfiguracja zawiera tylko nazwę zmiennej HYDROWOLSKI_TOKEN.</p>
            <label className="field"><span className="field-label">Nowy token</span>
              <input className="input font-mono" value={secret} readOnly autoComplete="off"
                onFocus={event => event.currentTarget.select()} /></label>
            <div className="flex flex-wrap gap-2">
              <button className="btn btn-primary btn-sm" onClick={() => void copy(secret, 'token')}>
                {copied === 'token' ? 'Skopiowano' : 'Kopiuj token'}</button>
              <button className="btn btn-secondary btn-sm" onClick={() => setSecret(null)}>Zapisano - ukryj</button>
            </div>
          </div>}
          <form onSubmit={event => void create(event)} className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="field"><span className="field-label">Nazwa agenta</span>
                <input className="input" value={name} maxLength={80} required placeholder="np. Claude Code na laptopie"
                  onChange={event => setName(event.target.value)} /></label>
              <label className="field"><span className="field-label">Ważność (dni, od 1 do 90)</span>
                <input className="input" type="number" min={1} max={90} required value={days}
                  onChange={event => setDays(Number(event.target.value))} /></label>
            </div>
            <fieldset><legend className="field-label mb-2">Zakres dostępu</legend>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">{SCOPES.map(scope => {
                const available = user?.permissions?.includes(scope.code)
                  && (!('requires' in scope) || user?.permissions?.includes(scope.requires));
                return <label key={scope.code} className={'flex items-center gap-2 ' + (available ? '' : 'text-ink-400')}>
                  <input type="checkbox" disabled={!available || busy} checked={scopes.includes(scope.code)}
                    onChange={event => toggle(scope.code, event.target.checked)} />
                  {scope.label}{!available && ' (brak uprawnienia)'}
                </label>;
              })}</div>
              <p className="text-ink-500 mt-2">Aby agent mógł wyszukać klienta przy dodawaniu ujęcia,
                zaznacz także „Odczyt klientów”.</p>
            </fieldset>
            <button type="submit" className="btn btn-primary btn-sm"
              disabled={busy || loading || !name.trim() || scopes.length === 0 || secret !== null}>
              {busy ? 'Zapisuję…' : 'Utwórz token'}</button>
          </form>
          <div className="border border-ink-100 rounded-md p-3 space-y-3">
            <h3 className="font-semibold">Podłączenie Claude Code</h3>
            <p>1. Dodaj połączenie w terminalu. Polecenie zapisuje adres i odwołanie do zmiennej,
              bez wartości tokenu.</p>
            <pre className="rounded bg-ink-50 p-3 text-xs whitespace-pre-wrap break-all">{addCommand}</pre>
            <button className="btn btn-secondary btn-sm" onClick={() => void copy(addCommand, 'command')}>
              {copied === 'command' ? 'Skopiowano polecenie' : 'Kopiuj polecenie konfiguracji'}</button>
            <p>2. Uruchom Claude Code poleceniem poniżej (macOS/Linux). Wklej token po pytaniu „Token MCP:”
              i naciśnij Enter. Wpisany sekret jest ukryty i nie trafia do historii poleceń.</p>
            <pre className="rounded bg-ink-50 p-3 text-xs whitespace-pre-wrap break-all">{LAUNCH_COMMAND}</pre>
            <button className="btn btn-secondary btn-sm" onClick={() => void copy(LAUNCH_COMMAND, 'launch')}>
              {copied === 'launch' ? 'Skopiowano polecenie' : 'Kopiuj polecenie uruchomienia'}</button>
            <p>3. W Claude Code otwórz <code>/mcp</code>, a następnie poproś o sprawdzenie
              połączenia narzędziem <code>portal_connection</code> i wyszukanie klienta.</p>
            <details>
              <summary className="cursor-pointer font-medium">Konfiguracja JSON dla pliku .mcp.json</summary>
              <p className="mt-2">To alternatywa dla polecenia z kroku 1. Dołącz wpis do istniejącego pliku
                w katalogu projektu. Zachowaj odwołanie do zmiennej zamiast wpisywać sekret.</p>
              <pre className="my-2 rounded bg-ink-50 p-3 text-xs whitespace-pre-wrap break-all">{configJson}</pre>
              <button className="btn btn-secondary btn-sm" onClick={() => void copy(configJson, 'config')}>
                {copied === 'config' ? 'Skopiowano JSON' : 'Kopiuj konfigurację JSON'}</button>
            </details>
            <a className="text-water-700 underline" href="https://code.claude.com/docs/en/mcp" target="_blank" rel="noreferrer">
              Dokumentacja połączeń MCP w Claude Code</a>
          </div>
          <div>
            <h3 className="font-semibold mb-2">Twoje tokeny</h3>
            {loading ? <p>Ładowanie…</p> : tokens.length === 0 ? <p className="text-ink-500">Nie masz jeszcze tokenów.</p> :
              <ul className="space-y-3">{tokens.map(token => {
                const expired = new Date(token.expiresAt).getTime() <= Date.now();
                const inactive = Boolean(token.revokedAt) || expired;
                const missing = inactive ? null : missingMcpScopesLabel(token.scopes, user?.permissions);
                return <li key={token.id} className="border border-ink-100 rounded-md p-3">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div><div className="font-semibold">{token.name}</div>
                      <div className="text-ink-500">{token.revokedAt ? `Unieważniony: ${date(token.revokedAt)}`
                        : expired ? `Wygasł: ${date(token.expiresAt)}` : `Ważny do: ${date(token.expiresAt)}`}</div>
                      <div className="text-ink-500">{token.scopes.map(code => SCOPES.find(scope => scope.code === code)?.label ?? code).join(', ')}</div>
                      {missing && <div className="text-amber-800">Nie obejmuje: {missing} – utwórz nowy token z tymi zakresami, aby je dodać.</div>}
                    </div>
                    {!inactive && (revokeId === token.id ? <div className="flex items-center gap-2">
                      <button className="btn btn-danger btn-sm" disabled={busy} onClick={() => void revoke(token.id)}>Potwierdź unieważnienie</button>
                      <button className="btn btn-secondary btn-sm" disabled={busy} onClick={() => setRevokeId(null)}>Anuluj</button>
                    </div> : <button className="btn btn-secondary btn-sm" disabled={busy}
                      onClick={() => setRevokeId(token.id)}>Unieważnij</button>)}
                  </div>
                </li>;
              })}</ul>}
          </div>
        </>}
      </div>
    </section>
  );
}
